// Índice de completitud de HC del consultorio (calidadHcMuestreo +
// calidadHcPaciente) — panel de ADMIN. Movido 2026-09-13 desde
// dashboard/FormulariosPendientes.tsx: el Dashboard exige sesión de Medplum,
// que el admin del HCE no tiene, así que ahí nunca se veía. Vive ahora en
// /alertas junto a las alertas en vivo, con la sesión de leerSesionHceAdmin.
//
// Sin llamadas a Medplum a propósito (la ruta de admin no tiene sesión de
// Medplum): el muestreo solo trae el Patient.id, y se muestra así, con la
// misma aclaración `idSinNombre` que ya usaba el widget.
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { SesionHceAdmin } from '@/auth/hceAdminSession';
import { obtenerCalidadHcMuestreo, obtenerCalidadHcPaciente, type IndiceCompletitud, type ResultadoMuestreo } from '@/lib/calidadHc';
import { VARIANTE_POR_ESTADO } from '@/auditoria/ChecklistItems';

/** Tamaño de muestra chico a propósito (pedido explícito): fotografía representativa sin ser costosa, mismo orden de magnitud que TAMANO_MUESTRA_DEFECTO del backend. */
const TAMANO_MUESTRA = 15;

/** Verde/ámbar/rojo por umbral — mismo criterio de 3 franjas que el resto del panel (StatusBadge de admin/shared.tsx en packages/web), sin inventar una cuarta. */
function colorDePorcentaje(porcentaje: number): string {
  if (porcentaje >= 80) return 'text-hce-pine';
  if (porcentaje >= 50) return 'text-amber-600';
  return 'text-destructive';
}

export function IndiceCompletitudHc({ sesion }: { sesion: SesionHceAdmin }): JSX.Element {
  const { t } = useTranslation();
  const [resultado, setResultado] = useState<ResultadoMuestreo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [pacienteAbierto, setPacienteAbierto] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    obtenerCalidadHcMuestreo(sesion.token, sesion.subdominio, TAMANO_MUESTRA)
      .then((r) => {
        if (!cancelado) {
          setResultado(r);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelado) setError(true);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [sesion]);

  const huecos = (resultado?.resultados ?? [])
    .filter((r) => r.score.porcentaje !== null && r.score.porcentaje < 100)
    .sort((a, b) => (a.score.porcentaje ?? 0) - (b.score.porcentaje ?? 0));

  return (
    <div className="rounded-lg border border-border bg-hce-surface p-4">
      <p className="text-sm font-medium text-hce-ink">{t('dashboard.calidadHc.titulo')}</p>

      {cargando && <p className="mt-1 text-xs text-hce-muted">{t('dashboard.calidadHc.cargando')}</p>}
      {!cargando && error && <p className="mt-1 text-xs text-destructive">{t('dashboard.calidadHc.error')}</p>}

      {!cargando && resultado && resultado.indiceConsultorio.porcentaje === null && (
        <p className="mt-1 text-xs text-hce-muted">{t('dashboard.calidadHc.sinDatos')}</p>
      )}

      {!cargando && resultado && resultado.indiceConsultorio.porcentaje !== null && (
        <>
          <div className="mt-1 flex items-baseline gap-2">
            <span className={`text-2xl font-semibold tabular-nums ${colorDePorcentaje(resultado.indiceConsultorio.porcentaje)}`}>
              {resultado.indiceConsultorio.porcentaje}%
            </span>
            <span className="text-xs text-hce-muted">
              {t('dashboard.calidadHc.muestra', { evaluados: resultado.pacientesEvaluados, elegibles: resultado.pacientesElegibles })}
            </span>
          </div>

          <button type="button" onClick={() => setDetalleAbierto((v) => !v)} className="mt-1.5 text-xs text-hce-pine hover:underline">
            {detalleAbierto ? t('dashboard.calidadHc.ocultarDetalle') : t('dashboard.calidadHc.verDetalle')}
          </button>

          {detalleAbierto && (
            <div className="mt-2">
              {huecos.length === 0 ? (
                <p className="text-xs text-hce-muted">{t('dashboard.calidadHc.sinHuecos')}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dashboard.calidadHc.columnaPaciente')}</TableHead>
                      <TableHead>{t('dashboard.calidadHc.columnaPorcentaje')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {huecos.map((r) => (
                      <TableRow key={r.patientId} className="cursor-pointer" onClick={() => setPacienteAbierto(r.patientId)}>
                        <TableCell className="font-mono text-xs text-hce-ink">{r.patientId}</TableCell>
                        <TableCell className={`tabular-nums ${colorDePorcentaje(r.score.porcentaje ?? 0)}`}>{r.score.porcentaje}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </>
      )}

      {pacienteAbierto && <DetallePacienteHc sesion={sesion} patientId={pacienteAbierto} onClose={() => setPacienteAbierto(null)} />}
    </div>
  );
}

/** Detalle de completitud de UN paciente (calidadHcPaciente), abierto desde la fila de huecos — inline (Dialog), no una ruta nueva. */
function DetallePacienteHc({ sesion, patientId, onClose }: { sesion: SesionHceAdmin; patientId: string; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [detalle, setDetalle] = useState<IndiceCompletitud | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    setError(false);
    obtenerCalidadHcPaciente(sesion.token, sesion.subdominio, patientId)
      .then((d) => {
        if (!cancelado) setDetalle(d);
      })
      .catch(() => {
        if (!cancelado) setError(true);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [sesion, patientId]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm">{patientId}</DialogTitle>
        </DialogHeader>

        <p className="-mt-2 text-xs text-hce-muted">{t('dashboard.calidadHc.idSinNombre')}</p>

        {cargando && <p className="py-3 text-sm text-hce-muted">{t('dashboard.calidadHc.cargando')}</p>}
        {!cargando && error && <p className="py-3 text-sm text-destructive">{t('dashboard.calidadHc.errorDetalle')}</p>}

        {!cargando && !error && detalle && (
          <div className="space-y-3">
            <div className="space-y-2">
              {detalle.items.map((item) => (
                <div key={item.clave} className="rounded-lg border border-border bg-hce-surface p-3">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-sm font-medium text-hce-ink">{item.titulo}</span>
                    <Badge variant={VARIANTE_POR_ESTADO[item.estado]}>{t(`auditoria.estado.${item.estado}`)}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-hce-muted">{item.detalle}</p>
                </div>
              ))}
            </div>

            {/* Notas de enfermería: va aparte; el backend la suma al score solo si el paciente tuvo estancia IMP (calidad-hc.ts). */}
            <div className="rounded-lg border border-dashed border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <span className="text-sm font-medium text-hce-ink">{detalle.notasEnfermeria.titulo}</span>
                <Badge variant={VARIANTE_POR_ESTADO[detalle.notasEnfermeria.estado]}>{t(`auditoria.estado.${detalle.notasEnfermeria.estado}`)}</Badge>
              </div>
              <p className="mt-1 text-sm text-hce-muted">{detalle.notasEnfermeria.detalle}</p>
              <p className="mt-1 text-xs italic text-hce-muted">{t('dashboard.calidadHc.notasEnfermeriaAparte')}</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
